import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import { toLocalDateISO } from '@/lib/formatEventDate';
import { Search, Ban, Trash2, Shield, ArrowLeft, Download, FlaskConical, Wand2, Bell, UserPlus, X, Copy, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminUserNotificationsModal from '@/components/AdminUserNotificationsModal';

// Phase 3 (Feb 2026): new 5-tier model. Legacy values (free/monthly/annual/
// lifetime) are still rendered correctly via TIER_DISPLAY so any in-flight
// pre-migration accounts don't look broken.
const TIER_OPTIONS = [
  { value: 'open_play', label: 'Open Play' },
  { value: 'verified_player', label: 'Verified Player' },
  { value: 'instructor_starter', label: 'Instructor Starter' },
  { value: 'instructor_plus', label: 'Instructor Advantage' },
  { value: 'instructor_unlimited', label: 'Instructor Unlimited' },
];

const FILTER_CHIPS = [
  { value: 'all', label: 'All' },
  { value: 'open_play', label: 'Open Play' },
  { value: 'verified_player', label: 'Verified Player' },
  { value: 'instructor_starter', label: 'Instr. Starter' },
  { value: 'instructor_plus', label: 'Instr. Advantage' },
  { value: 'instructor_unlimited', label: 'Instr. Unlimited' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'testers', label: 'Testers' },
];

const LEGACY_TIERS = new Set(['free', 'monthly', 'annual', 'lifetime']);


export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [loading, setLoading] = useState(true);
  // Server-side pagination state. Page size locked to 50 for now —
  // covers the "loading 1k users every time" complaint without
  // forcing a UI choice on admins.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0); // zero-indexed
  const [total, setTotal] = useState(0);
  // Notification-prefs editor: holds the user id we're currently editing.
  // null = modal closed.
  const [notifUserId, setNotifUserId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Diagnostic lookup result (separate from the in-table search above)
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  // Debounce search so we don't fire a request on every keystroke.
  // 300ms feels snappy without spamming the backend during type-ahead.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Whenever the search OR tier filter changes, reset to page 0 so the
  // admin sees fresh results from the top instead of an empty page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filterTier]);

  // Fetch users server-side with pagination + filters baked in.
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      skip: String(page * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filterTier && filterTier !== 'all') params.set('tier', filterTier);
    api.get(`/admin/users?${params.toString()}`)
      .then(({ data }) => {
        setUsers(data.users || []);
        setTotal(data.total ?? 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [debouncedSearch, filterTier, page]);

  const handleBan = async (userId) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/ban`);
      setUsers(prev => prev.map(u => u.id === userId ? {...u, is_banned: data.is_banned} : u));
      toast.success(data.message);
    } catch { toast.error('Failed to update user'); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Permanently delete this user?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch { toast.error('Failed to delete user'); }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? {...u, role: newRole} : u));
      toast.success('Role updated');
    } catch { toast.error('Failed to update role'); }
  };

  const handleChangeTier = async (userId, newTier) => {
    try {
      await api.put(`/admin/users/${userId}`, { subscription_tier: newTier });
      setUsers(prev => prev.map(u => u.id === userId ? {...u, subscription_tier: newTier} : u));
      toast.success('Subscription updated');
    } catch { toast.error('Failed to update subscription'); }
  };

  const handleToggleTestAccount = async (user) => {
    const flagging = !user.is_test_account;
    const verb = flagging ? 'flag' : 'unflag';
    if (!window.confirm(`${verb === 'flag' ? 'Flag' : 'Unflag'} ${user.email} as a test/coupon-tester account? ${flagging ? 'They will be hidden from player search and last-minute alerts.' : 'They will reappear in player search.'}`)) return;
    try {
      const { data } = await api.post(`/admin/users/${user.id}/test-account`);
      setUsers(prev => prev.map(u => u.id === user.id ? {...u, is_test_account: data.is_test_account} : u));
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update test flag');
    }
  };

  const handleAutoFlagTesters = async () => {
    if (!window.confirm('Auto-flag all users with test-looking emails (e.g. *@test.com, "test" in local part, *+test@*) as test accounts? They will be hidden from player search.')) return;
    try {
      const { data } = await api.post('/admin/users/auto-flag-testers');
      toast.success(data.message);
      if (data.flagged_count > 0) {
        // Re-fetch the current page so newly flagged users render the
        // tester pill correctly. Re-trigger via state nudge so we hit
        // the paginated endpoint with the same filters.
        setPage(p => p); // no-op state set forces effect dep re-eval? Use the next line instead:
        const params = new URLSearchParams({ skip: String(page * PAGE_SIZE), limit: String(PAGE_SIZE) });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (filterTier && filterTier !== 'all') params.set('tier', filterTier);
        const fresh = await api.get(`/admin/users?${params.toString()}`);
        setUsers(fresh.data.users || []);
        setTotal(fresh.data.total ?? 0);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-flag failed');
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await api.get('/admin/users/export.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const today = toLocalDateISO().replaceAll('-', '');
      a.download = `mfm-users-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      // Surface the real error instead of a generic toast — easier to triage
      // when something genuinely fails (CORS, expired token, server error).
      const msg = err.response?.status
        ? `Export failed: HTTP ${err.response.status}`
        : `Export failed: ${err.message || 'network error'}`;
      toast.error(msg);
    }
  };

  // Server-side filter + pagination means the table renders exactly
  // what the backend returned. No more client-side narrowing required.
  const filtered = users;

  const handleLookup = async () => {
    const email = search.trim();
    if (!email.includes('@')) { toast.error('Type a full email address to look up.'); return; }
    setLookupBusy(true);
    setLookupResult(null);
    try {
      const { data } = await api.get(`/admin/users/lookup?email=${encodeURIComponent(email)}`);
      setLookupResult(data);
      if (!data.exists) toast.error('No account exists with that exact email.');
      else toast.success('Account found — see details below.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lookup failed');
    } finally { setLookupBusy(false); }
  };

  // Initial-load spinner only — for subsequent page/filter loads we
  // keep the table visible and show a small inline loader so the UI
  // doesn't flash blank on every Prev/Next click.
  const initialLoading = loading && users.length === 0 && total === 0;
  if (initialLoading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="user-management-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/admin"><Button variant="ghost" size="sm" className="rounded-full"><ArrowLeft className="w-4 h-4" /></Button></Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>User Management</h1>
          <p className="text-slate-500 mt-1" data-testid="admin-users-total-label">
            {total.toLocaleString()} total user{total === 1 ? '' : 's'}
            {(debouncedSearch || filterTier !== 'all') && <span className="text-slate-400"> · matching current filter</span>}
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          size="sm"
          className="rounded-full gap-2 bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]"
          data-testid="admin-invite-user-btn"
        >
          <UserPlus className="w-4 h-4" /> Invite User
        </Button>
        <Button
          onClick={handleAutoFlagTesters}
          variant="outline"
          size="sm"
          className="rounded-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="admin-auto-flag-testers-btn"
          title="Hide all *@test.com / *+test@* / test-looking accounts from player search"
        >
          <Wand2 className="w-4 h-4" /> Auto-flag testers
        </Button>
        <Button
          onClick={handleExportCsv}
          variant="outline"
          size="sm"
          className="rounded-full gap-2 border-[#56dfb4] text-slate-700 hover:bg-[#56dfb4]/10"
          data-testid="admin-export-users-csv-btn"
        >
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..." className="pl-10 rounded-xl" data-testid="admin-search-users" />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleLookup}
          disabled={lookupBusy || !search.includes('@')}
          className="rounded-full gap-1.5"
          data-testid="admin-lookup-user-btn"
          title="Look up a user by exact email — bypasses dashboard filters and pagination. Useful when someone says they signed up but you can't find them."
        >
          🔎 Look up by email
        </Button>
        <div className="flex flex-wrap gap-2">
          {FILTER_CHIPS.map(t => (
            <button key={t.value} onClick={() => setFilterTier(t.value)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${filterTier===t.value?'bg-[#56dfb4] text-slate-900 border-[#56dfb4]':'border-slate-200 hover:border-slate-300'}`}
              data-testid={`admin-users-filter-${t.value}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Diagnostic lookup result */}
      {lookupResult && (
        <Card className="border-amber-200 bg-amber-50/50 mb-4" data-testid="admin-lookup-result">
          <CardContent className="p-4 space-y-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium">
                Lookup for <span className="font-mono">{lookupResult.email_searched}</span>:
                {lookupResult.exists
                  ? <span className="text-green-700"> ✅ Account exists</span>
                  : <span className="text-red-600"> ❌ No account with that exact email</span>}
              </p>
              <button onClick={() => setLookupResult(null)} className="text-xs text-slate-400 hover:text-slate-600 underline">Dismiss</button>
            </div>
            {lookupResult.exists ? (
              <div className="text-xs text-slate-600 space-y-1">
                <p>• User ID: <span className="font-mono">{lookupResult.user.id}</span> · Tier: <strong>{lookupResult.user.subscription_tier}</strong></p>
                <p>• Created: {lookupResult.user.created_at} · Last login: {lookupResult.user.last_login || '(never)'}</p>
                <p>• Flags: {[
                  lookupResult.user.is_banned && 'banned',
                  lookupResult.user.is_test_account && 'test_account',
                  lookupResult.user.is_demo && 'demo',
                  lookupResult.user.email_excluded && 'email_excluded',
                  lookupResult.user.must_change_password && 'must_change_password',
                  lookupResult.user.must_choose_tier && 'must_choose_tier',
                ].filter(Boolean).join(', ') || '(none)'}</p>
                <Link to={`/admin/users/${lookupResult.user.id}`} className="inline-block mt-1 text-[#df5681] underline">Open full profile →</Link>
              </div>
            ) : (
              <p className="text-xs text-slate-600">
                Suggestions: Ask the user to confirm the exact email they typed (watch for typos, missing letters, or different domains like <span className="font-mono">.con</span> vs <span className="font-mono">.com</span>). The lookup is case-insensitive and trims whitespace, so casing isn't an issue.
              </p>
            )}
            {lookupResult.recent_failed_login_attempts?.length > 0 && (
              <div className="text-xs text-slate-600 mt-2 pt-2 border-t border-amber-200">
                <p className="font-medium mb-1">⚠️ Recent failed login attempts targeting this email:</p>
                {lookupResult.recent_failed_login_attempts.map((a, i) => (
                  <p key={i}>• {a.count} attempt{a.count === 1 ? '' : 's'} from {a.ip} {a.lockout_until && `(locked out until ${a.lockout_until})`}</p>
                ))}
                <p className="mt-1 italic">If this user exists but can't log in, they're likely typing the wrong password. Consider resetting it for them.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="border-slate-100">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(u => (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <TableCell>
                    <Link to={`/admin/users/${u.id}`} className="flex items-center gap-2 hover:text-[#b456df] transition-colors group" data-testid={`admin-user-name-link-${u.id}`}>
                      <div className="w-8 h-8 rounded-full bg-[#56dfb4]/15 flex items-center justify-center text-xs font-bold">
                        {(u.display_name || 'U')[0].toUpperCase()}
                      </div>
                      <span className="font-medium text-sm group-hover:underline">{u.display_name || `${u.first_name} ${u.last_name}`}</span>
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    <Link to={`/admin/users/${u.id}`} className="hover:underline hover:text-[#b456df]" data-testid={`admin-user-email-link-${u.id}`}>
                      {u.email}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const currentTier = (u.subscription_tier || 'open_play').toLowerCase();
                      const isLegacy = LEGACY_TIERS.has(currentTier);
                      const displayTier = isLegacy
                        ? (u.is_instructor ? 'instructor_starter' : 'verified_player')
                        : currentTier;
                      return (
                        <div className="flex items-center gap-1.5">
                          <select
                            value={displayTier}
                            onChange={e => handleChangeTier(u.id, e.target.value)}
                            className="text-xs border rounded-lg px-2 py-1"
                            data-testid={`admin-tier-select-${u.id}`}
                          >
                            {TIER_OPTIONS.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <select value={u.role || 'user'} onChange={e => handleChangeRole(u.id, e.target.value)}
                      className="text-xs border rounded-lg px-2 py-1 capitalize" data-testid={`admin-role-select-${u.id}`}>
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={u.is_banned ? 'destructive' : 'secondary'} className="rounded-full text-xs">
                        {u.is_banned ? 'Banned' : 'Active'}
                      </Badge>
                      {u.is_test_account && (
                        <Badge className="rounded-full text-xs bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`admin-tester-badge-${u.id}`}>
                          Tester
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setNotifUserId(u.id)}
                        className="text-[#56c6df]"
                        title="Manage notification preferences"
                        data-testid={`admin-notif-btn-${u.id}`}
                      >
                        <Bell className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleTestAccount(u)}
                        className={u.is_test_account ? 'text-amber-600' : 'text-slate-400 hover:text-amber-600'}
                        title={u.is_test_account ? 'Unflag as test account (re-show in player search)' : 'Flag as test/coupon-tester (hide from player search)'}
                        data-testid={`admin-toggle-test-${u.id}`}
                      >
                        <FlaskConical className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleBan(u.id)} className={`${u.is_banned ? 'text-green-500' : 'text-amber-500'}`} data-testid={`admin-ban-${u.id}`}>
                        <Ban className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="text-red-500" data-testid={`admin-delete-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
        {/* Pagination footer — server returns 50 rows per page so admins
            don't pay the cost of loading the entire user list every
            time they open this screen. */}
        {total > PAGE_SIZE && (
          <div
            className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 text-sm"
            data-testid="admin-users-pagination"
          >
            <p className="text-slate-500" data-testid="admin-users-page-range">
              Showing <strong>{total === 0 ? 0 : page * PAGE_SIZE + 1}</strong>–<strong>{Math.min((page + 1) * PAGE_SIZE, total)}</strong> of <strong>{total.toLocaleString()}</strong>
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={page === 0 || loading}
                onClick={() => setPage(p => Math.max(0, p - 1))}
                data-testid="admin-users-prev-page-btn"
              >
                ← Prev
              </Button>
              <span className="text-slate-400 text-xs px-1" data-testid="admin-users-page-indicator">
                Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={(page + 1) * PAGE_SIZE >= total || loading}
                onClick={() => setPage(p => p + 1)}
                data-testid="admin-users-next-page-btn"
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </Card>

      {notifUserId && (
        <AdminUserNotificationsModal
          userId={notifUserId}
          onClose={() => setNotifUserId(null)}
          onSaved={(updated) => {
            // Patch the table row so phone/preferences shown elsewhere
            // stay in sync without a refetch.
            if (updated && updated.id) {
              setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
            }
          }}
        />
      )}

      {inviteOpen && (
        <InviteUserModal
          onClose={() => setInviteOpen(false)}
          onInvited={(newUser) => {
            setUsers(prev => [newUser, ...prev]);
          }}
        />
      )}
    </div>
  );
}

function InviteUserModal({ onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [tempPassword, setTempPassword] = useState(() => generateTempPassword());
  const [isInstructor, setIsInstructor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // { email, temp_password } after success

  const submit = async () => {
    if (!email.trim()) { toast.error('Enter the new user\'s email.'); return; }
    if (tempPassword.length < 6) { toast.error('Temporary password must be at least 6 characters.'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/admin/users/invite', {
        email: email.trim().toLowerCase(),
        temp_password: tempPassword,
        is_instructor: isInstructor,
      });
      toast.success(data.message || 'User invited');
      setCreated({ email: email.trim().toLowerCase(), temp_password: tempPassword });
      onInvited?.(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite user');
    } finally { setSubmitting(false); }
  };

  const copyAll = async () => {
    const text = `Email: ${created.email}\nTemporary password: ${created.temp_password}\n\nLog in at https://meetformahjong.com/login — you'll be prompted to change your password on first login.`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Login details copied to clipboard');
    } catch {
      toast.error('Couldn\'t copy — please copy manually.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="invite-user-modal">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <UserPlus className="w-5 h-5 text-[#56dfb4]" /> Invite a User
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">They'll change the temp password and finish their profile after first login.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="invite-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {created ? (
            <>
              <div className="rounded-xl bg-[#56dfb4]/10 border border-[#56dfb4]/30 p-4 space-y-2 text-sm">
                <p className="font-medium text-slate-800">✓ Invitation created — share these credentials with the user:</p>
                <div className="font-mono text-sm bg-white rounded-lg p-3 border border-slate-200" data-testid="invite-created-creds">
                  <p><span className="text-slate-400">Email:</span> {created.email}</p>
                  <p><span className="text-slate-400">Temporary password:</span> {created.temp_password}</p>
                </div>
                <p className="text-xs text-slate-500">On first login they'll be asked to change this password before doing anything else.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={copyAll} variant="outline" className="rounded-full flex-1 gap-2" data-testid="invite-copy-creds">
                  <Copy className="w-4 h-4" /> Copy login details
                </Button>
                <Button onClick={onClose} className="rounded-full flex-1 bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]" data-testid="invite-done">Done</Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium block mb-1">Email *</label>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="rounded-xl"
                  data-testid="invite-email-input"
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Temporary password *</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={tempPassword}
                    onChange={e => setTempPassword(e.target.value)}
                    className="rounded-xl font-mono"
                    data-testid="invite-password-input"
                    autoComplete="off"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setTempPassword(generateTempPassword())}
                    className="rounded-xl flex-shrink-0"
                    data-testid="invite-regen-password"
                    title="Generate a new random password"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">At least 6 characters. The user changes this on first login.</p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={isInstructor}
                  onChange={e => setIsInstructor(e.target.checked)}
                  data-testid="invite-is-instructor"
                />
                Invite as Instructor (starts on Instructor Starter tier)
              </label>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={onClose} className="rounded-full flex-1" data-testid="invite-cancel">Cancel</Button>
                <Button onClick={submit} disabled={submitting} className="rounded-full flex-1 bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]" data-testid="invite-submit">
                  {submitting ? 'Creating…' : 'Create User'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Generate a friendly, copy/paste-able temp password — readable enough
// to dictate over the phone but not so simple that it's brute-forceable.
function generateTempPassword() {
  const adjectives = ['Bamboo', 'Crak', 'Dot', 'Joker', 'Wind', 'Dragon', 'Flower'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const num = Math.floor(1000 + Math.random() * 9000);
  return `${adj}${num}!`;
}
